import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Loader2, Send, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { CREDIT_META, CreditLogList } from "@/components/CreditDialog";

const KIND_LABEL = { manual: "Manuel", reminder: "Hatırlatma", auto_reminder: "Otomatik Hatırlatma" };

const BalanceCards = ({ credits }) => (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        {Object.entries(CREDIT_META).map(([k, m]) => (
            <Card key={k}>
                <CardContent className="p-5 flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl border flex items-center justify-center ${m.color}`}>
                        <m.icon className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-wider text-slate-500">{m.label} Kontürü</div>
                        <div className="text-2xl font-bold mono text-slate-900" data-testid={`balance-${k}`}>
                            {credits?.[m.field] ?? 0}
                        </div>
                    </div>
                </CardContent>
            </Card>
        ))}
    </div>
);

const MessageRow = ({ m }) => (
    <div className="py-3 flex items-start justify-between gap-3 border-b last:border-b-0" data-testid={`message-row-${m.id}`}>
        <div className="min-w-0">
            <div className="text-sm font-medium text-slate-800">
                {CREDIT_META[m.channel]?.label} • {m.patient_name || m.to}
                <span className="ml-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                    {KIND_LABEL[m.kind] || m.kind}
                </span>
            </div>
            <div className="text-sm text-slate-600 mt-0.5 break-words">{m.body}</div>
            {m.error && <div className="text-xs text-rose-600 mt-1">{m.error}</div>}
            <div className="text-xs text-slate-400 mt-1">{m.created_at?.slice(0, 16).replace("T", " ")} • {m.to}</div>
        </div>
        {m.status === "sent" ? (
            <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" title="Gönderildi" />
        ) : (
            <XCircle className="w-4 h-4 text-rose-500 flex-shrink-0" title="Gönderilemedi" />
        )}
    </div>
);

export default function Messages() {
    const [credits, setCredits] = useState(null);
    const [messages, setMessages] = useState([]);
    const [patients, setPatients] = useState([]);
    const [form, setForm] = useState({ channel: "whatsapp", patient_id: "", phone: "", body: "" });
    const [sending, setSending] = useState(false);

    async function load() {
        const [c, m, p] = await Promise.all([api.get("/clinic/credits"), api.get("/messages"), api.get("/patients")]);
        setCredits(c.data);
        setMessages(m.data);
        setPatients(p.data);
    }

    useEffect(() => {
        load();
    }, []);

    async function submit(e) {
        e.preventDefault();
        setSending(true);
        try {
            const payload = { channel: form.channel, body: form.body };
            if (form.patient_id) payload.patient_id = form.patient_id;
            else payload.phone = form.phone;
            const { data } = await api.post("/messages", payload);
            if (data.status === "sent") {
                toast.success("Mesaj gönderildi");
                setForm({ ...form, body: "" });
            } else toast.error(`Gönderilemedi: ${data.error || ""} (kontür iade edildi)`);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSending(false);
        }
    }

    return (
        <div data-testid="messages-page">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-slate-900">Mesajlar & Kontür</h1>
                <p className="text-slate-600 mt-1">Hastalarınıza SMS / WhatsApp gönderin, kontür bakiyenizi takip edin</p>
            </div>
            <BalanceCards credits={credits} />
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                <Card className="lg:col-span-2">
                    <CardContent className="p-6">
                        <h2 className="text-lg font-semibold text-slate-900 mb-4">Yeni Mesaj</h2>
                        <form onSubmit={submit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-2">
                                {["whatsapp", "sms"].map((k) => {
                                    const M = CREDIT_META[k];
                                    return (
                                        <Button key={k} type="button" variant={form.channel === k ? "default" : "outline"}
                                            className={form.channel === k ? "dk-btn-primary" : ""}
                                            onClick={() => setForm({ ...form, channel: k })} data-testid={`channel-${k}`}>
                                            <M.icon className="w-4 h-4 mr-1" /> {M.label}
                                        </Button>
                                    );
                                })}
                            </div>
                            <div>
                                <Label>Hasta</Label>
                                <Select value={form.patient_id || "none"} onValueChange={(v) => setForm({ ...form, patient_id: v === "none" ? "" : v })}>
                                    <SelectTrigger data-testid="message-patient-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">— Manuel numara gir —</SelectItem>
                                        {patients.map((p) => (
                                            <SelectItem key={p.id} value={p.id}>{p.name} {p.phone ? `(${p.phone})` : "(tel yok)"}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            {!form.patient_id && (
                                <div>
                                    <Label>Telefon</Label>
                                    <Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                        placeholder="05XX XXX XX XX" data-testid="message-phone-input" />
                                </div>
                            )}
                            <div>
                                <Label>Mesaj</Label>
                                <Textarea required rows={4} maxLength={1000} value={form.body}
                                    onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="message-body-input" />
                                <div className="text-xs text-slate-500 mt-1">{form.body.length}/1000 • 1 mesaj = 1 kontür</div>
                            </div>
                            <Button type="submit" className="dk-btn-primary w-full" disabled={sending} data-testid="message-send-btn">
                                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" />Gönder</>}
                            </Button>
                        </form>
                    </CardContent>
                </Card>
                <div className="lg:col-span-3 space-y-6">
                    <Card>
                        <CardContent className="p-6">
                            <h2 className="text-lg font-semibold text-slate-900 mb-2">Gönderim Geçmişi</h2>
                            <div className="max-h-96 overflow-y-auto" data-testid="message-history">
                                {messages.length === 0 && <div className="py-6 text-center text-slate-500">Henüz mesaj yok.</div>}
                                {messages.map((m) => <MessageRow key={m.id} m={m} />)}
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-6">
                            <h2 className="text-lg font-semibold text-slate-900 mb-2">Kontür Hareketleri</h2>
                            <CreditLogList logs={credits?.logs || []} />
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
